import React, { ChangeEvent, ForwardedRef, forwardRef, KeyboardEvent, useEffect, useImperativeHandle, useRef, useState, FocusEvent, PointerEvent, useMemo} from "react";
import CloseIcon from "../Icons/CloseIcon";
import IconButton from "../Button/IconButton";
import GUI, { IGUIProps } from "../GUI";
import { UpdateEvent } from "../GUIEvents";
import { useFormControl } from "../Form/FormControl";

export class Formatters {
    static PositiveRealNumber(val:string){
        return val.replace(/[^\d\.]/g, "").replace(/^(\d*\.\d*)\..*$/, "$1")
    }
    static Evaluate(val:string){
        try {
            return eval(val)
        }catch(e){
            return val
        }
    }
}
export class Evaluators {
    static Number(val:string){
        if (/^[\d\.\-\+\/\*\(\)]+$/.test(val)) try {
            const value = parseFloat(eval(val))
            return value.toString()
        }catch(e){}
        return val
    }
}

export type TInputElement = HTMLInputElement|HTMLTextAreaElement
export type TInputUpdateHandler = (e:UpdateEvent<string,TInputElement>) => void
export type TTextInputProps = IGUIProps & {
    id?:string
    readOnly?:boolean
    value?:string
    defaultValue?:string
    inputProps?:React.ComponentPropsWithoutRef<"input">
    placeholder?:string
    float?:boolean
    canClear?:boolean
    multiline?:boolean

    "follow-id"?:string                         // anchor

    evaluator?:(text:string) => string          // change text on blur
    formatter?:(text:string) => string          // chagne text on change
    onFocus?:(e:FocusEvent<HTMLInputElement>) => void
    onBlur?:(e:FocusEvent<HTMLInputElement>) => void
    onSubmit?:(e:KeyboardEvent<TInputElement>, text:string) => void
    onKeyDown?:(e:KeyboardEvent<TInputElement>) => void
    onPointerDown?:(e:PointerEvent<TInputElement>) => void

    onUpdate?:TInputUpdateHandler
    preIcon?:JSX.Element|JSX.Element[]
    endIcon?:JSX.Element|JSX.Element[]

    // form
    onValid?:(valid:boolean) => void
    onDirty?:(dirty:boolean) => void
}
export interface ITextInput {
    value:string|undefined
    focus:Function
    setAttribute:(key:string, value:string) => void
    input:HTMLInputElement|HTMLTextAreaElement|null

    // form
    valid:boolean
    dirty:boolean
    validate:(val:string|undefined, markAsDirty?:boolean) => void
}

var g_id = 0
export default forwardRef((props:TTextInputProps,ref?:ForwardedRef<ITextInput>) => {
    const isManaged = props.hasOwnProperty("value")
    const [value,setValue] = useState<string>(props.value ?? props.defaultValue ?? "" )
    const current = isManaged ? props.value! : value

    const [float,setFloat] = useState<boolean>(!!props.float && !!current)
    const inputRef = useRef<HTMLInputElement|HTMLTextAreaElement|null>(null)
    const selfRef = useRef<HTMLDivElement|null>(null)
    const inputId = useMemo(() => props.inputProps?.id || `input_${g_id++}`, [])

    const {setValue:setFormValue,setDirty,dirty,valid,validate,ready} = useFormControl()

    const _notify = (e:Event, val:string) => inputRef.current && props.onUpdate?.(new UpdateEvent(e, val, inputRef.current))

    const _setValue = (e:Event|null, val:string) => {
        val = props.formatter ? props.formatter(val) : val
        setFormValue?.(val||"")        
        if (!isManaged) setValue(val||"")
        setFloat(!!props.float && !!val)
        e && _notify(e, val)
    }
    const onChange = (e:ChangeEvent<HTMLInputElement>) => _setValue(e.nativeEvent, e.target.value)
    const onFocus = (e:FocusEvent<HTMLInputElement>) => {
        props.onFocus?.(e)
        if (props.float) setFloat(true)
    }
    const onBlur = (e:FocusEvent<HTMLInputElement>) => {
        props.onBlur?.(e)
        if (props.evaluator) {
            const val = props.evaluator(current)
            if (val!=current) _setValue(e.nativeEvent, val)
        }
        setDirty?.(true)
        if (props.float && !current) setFloat(false)
    }
    const onClear = (e:React.MouseEvent<HTMLButtonElement>) => _setValue(e.nativeEvent, "")
    const onKeyDown = (e:KeyboardEvent<HTMLInputElement>) => {
        props.onKeyDown?.(e)
        e.key =="Enter" && props.onSubmit?.(e, current)
    }
    const onPointerDown = (e:PointerEvent<HTMLInputElement>) => props.onPointerDown?.(e)
    
    useImperativeHandle(ref, () => ({
        get value() { return inputRef.current?.value }, 
        get input() { return inputRef.current||null },
        setAttribute(key:string, value:string) { selfRef.current?.setAttribute(key, value) },
        focus(){ inputRef.current?.focus() },
        valid,dirty,
        validate(val:string|undefined, markAsDirty:boolean=false) { return validate(val, !!markAsDirty) }
    }), [current])

    useEffect(() => {props.onValid?.(valid)}, [valid])
    useEffect(() => {props.onDirty?.(dirty)}, [dirty])
    useEffect(() => {isManaged && ready && setFormValue?.(props.value)}, [props.value,ready])    
    useEffect(() => {!isManaged && ready && setFormValue?.(props.defaultValue)}, [props.defaultValue,ready])

    const inputProps : any = { ref:inputRef, value:current, onFocus, onBlur, onKeyDown, onPointerDown, onChange, readOnly:props.readOnly, ...props.inputProps}
    if (!inputProps.id) inputProps.id = inputId
    if (!inputProps.name) inputProps.name = inputId
    const input = props.multiline ? <textarea {...inputProps}/> : <input {...inputProps}/>
    const placeholder = props.placeholder && (!current || float) ? <div className={GUI.joinClass("placeholder", float?"float":"")}>{props.placeholder}</div> : null
    const id = props.id ? {id:props.id} : {}
    const followId = props["follow-id"] ? {"follow-id":props["follow-id"]} : {}
    const preIcons = props.preIcon ? <div className="flex middle gap-2 preIcons">{props.preIcon}</div> : <></>
    const endIcons = (props.canClear && !!current) || props.endIcon ? 
        <div className="flex middle gap-2 endIcons">
            {props.canClear && !!current ? <IconButton className="small" onClick={onClear}><CloseIcon/></IconButton> : <></>}
            {props.endIcon instanceof Array ? props.endIcon : props.endIcon ?? <></>}
        </div> : <></>
    return <div className={GUI.joinClass("ginput", props.className, props.multiline ? "multiline" : "")} ref={selfRef} {...id} {...followId}>
        {preIcons}
        {input}
        {endIcons}
        {placeholder}
    </div>
})